import {
    HubConnectionBuilder,
    HubConnectionState,
    LogLevel,
    type IRetryPolicy,
    type RetryContext
} from "@microsoft/signalr";
import {
    useCallback,
    useEffect,
    useMemo,
    useState,
    type PropsWithChildren
} from "react";
import { API_BASE_URL } from "../../api/api";
import { AppStorage } from "../storage/appStorage";
import { SignalRContext } from "./signalRContext";

const reconnectPolicy: IRetryPolicy = {
    nextRetryDelayInMilliseconds: (retryContext: RetryContext): number => {
        if (retryContext.previousRetryCount === 0) {
            return 0;
        }

        const maxDelayMilliseconds = 20000;
        const exponentialDelayMilliseconds = 1000 * 2 ** Math.min(retryContext.previousRetryCount - 1, 5);
        const jitterMilliseconds = Math.floor(Math.random() * 1000);

        return Math.min(exponentialDelayMilliseconds, maxDelayMilliseconds) + jitterMilliseconds;
    }
};

export const SignalRProvider = ({
    children,
    hubUrl = `${API_BASE_URL}/hubs/application`,
    autoConnect = true
}: PropsWithChildren<{
    hubUrl?: string;
    autoConnect?: boolean;
}>) => {
    const connection = useMemo(() => new HubConnectionBuilder()
        .withUrl(hubUrl, {
            withCredentials: false,
            accessTokenFactory: () => `${btoa(AppStorage.getAuthToken() ?? "")}`
        })
        .withAutomaticReconnect(reconnectPolicy)
        .configureLogging(LogLevel.Information)
        .build(), [hubUrl]);

    const [connectionState, setConnectionState] = useState(connection.state);

    useEffect(() => {
        let isMounted = true;
        const updateConnectionState = (state: HubConnectionState): void => {
            if (isMounted) {
                setConnectionState(state);
            }
        };

        connection.onclose(() => {
            updateConnectionState(HubConnectionState.Disconnected);
        });

        connection.onreconnecting(() => {
            updateConnectionState(HubConnectionState.Reconnecting);
        });

        connection.onreconnected(() => {
            updateConnectionState(HubConnectionState.Connected);
        });

        return () => {
            isMounted = false;
            void connection.stop();
        };
    }, [connection]);

    const startConnection = useCallback(async () => {
        if (connection.state === HubConnectionState.Disconnected) {
            await connection.start();
            setConnectionState(connection.state);
        }

        return connection;
    }, [connection]);

    const stopConnection = useCallback(async () => {
        if (connection.state !== HubConnectionState.Disconnected) {
            await connection.stop();
            setConnectionState(HubConnectionState.Disconnected);
        }
    }, [connection]);

    useEffect(() => {
        if (!autoConnect) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            void startConnection();
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [autoConnect, startConnection]);

    const value = useMemo(() => ({
        connection,
        connectionState,
        startConnection,
        stopConnection
    }), [connection, connectionState, startConnection, stopConnection]);

    return (
        <SignalRContext.Provider value={value}>
            {children}
        </SignalRContext.Provider>
    );
};
